import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Plus, Trash2, Save, Send, ArrowLeft, DollarSign, TrendingUp, AlertTriangle } from 'lucide-react';
import { useCrmOrgStore } from '../../store/crmOrg.store';
import quotesService, { Quote, QuoteItem, CreateQuoteItemInput } from '../../services/crm/quotesService';
import productsService, { Product } from '../../services/crm/productsService';
import suppliersService, { Supplier } from '../../services/crm/suppliersService';
import dealsService, { Deal } from '../../services/crm/dealsService';

const QuoteBuilder: React.FC = () => {
  const { dealId, quoteId } = useParams();
  const navigate = useNavigate();
  const { activeOrgId } = useCrmOrgStore();

  const [deal, setDeal] = useState<Deal | null>(null);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [items, setItems] = useState<QuoteItem[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Quote settings
  const [validUntil, setValidUntil] = useState('');
  const [taxRate, setTaxRate] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [paymentTerms, setPaymentTerms] = useState('Net 30 days');
  const [deliveryTerms, setDeliveryTerms] = useState('');
  const [notes, setNotes] = useState('');
  const [createSupplierSolution, setCreateSupplierSolution] = useState(true);
  
  // Add item modal
  const [showAddItem, setShowAddItem] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [newItemDescription, setNewItemDescription] = useState('');
  const [newItemQuantity, setNewItemQuantity] = useState(1);
  const [newItemUnitCost, setNewItemUnitCost] = useState(0);
  const [newItemUnitPrice, setNewItemUnitPrice] = useState(0);
  const [newItemSupplier, setNewItemSupplier] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  useEffect(() => {
    if (activeOrgId) {
      loadData();
    }
  }, [activeOrgId, dealId, quoteId]);

  const loadData = async () => {
    if (!activeOrgId) return;
    
    try {
      setLoading(true);
      
      // Load deal
      if (dealId) {
        const dealRes = await dealsService.get(dealId, activeOrgId);
        setDeal(dealRes.data);
      }
      
      // Load or create quote
      if (quoteId) {
        const [quoteRes, itemsRes] = await Promise.all([
          quotesService.get(quoteId),
          quotesService.getItems(quoteId)
        ]);
        setQuote(quoteRes.data);
        setItems(itemsRes.data);
        setTaxRate(quoteRes.data.tax_rate || 0);
        setDiscountAmount(quoteRes.data.discount_amount || 0);
        setValidUntil(quoteRes.data.valid_until || '');
        setPaymentTerms(quoteRes.data.payment_terms || 'Net 30 days');
        setDeliveryTerms(quoteRes.data.delivery_terms || '');
        setNotes(quoteRes.data.notes || '');
        setCreateSupplierSolution(quoteRes.data.create_supplier_solution);
      } else {
        // Create new quote
        const newQuote = await quotesService.create({
          organization_id: activeOrgId,
          deal_id: dealId,
          create_supplier_solution: true,
          tax_rate: 0,
          discount_amount: 0
        });
        setQuote(newQuote.data);
        navigate(`/crm/deals/${dealId}/quotes/${newQuote.data.id}`, { replace: true });
      }
      
      // Load products and suppliers
      const [productsRes, suppliersRes] = await Promise.all([
        productsService.list(activeOrgId),
        suppliersService.list({ organization_id: activeOrgId, limit: 1000, offset: 0 })
      ]);
      setProducts(productsRes.data);
      setSuppliers(suppliersRes.data);
      
    } catch (error) {
      console.error('Error loading quote:', error);
      toast.error('Failed to load quote');
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = async () => {
    if (!quote || !newItemName) {
      toast.error('Please enter a product name');
      return;
    }

    try {
      const itemData: CreateQuoteItemInput = {
        quote_id: quote.id,
        name: newItemName,
        description: newItemDescription,
        quantity: newItemQuantity,
        unit_type: 'unit',
        unit_cost: newItemUnitCost,
        unit_price: newItemUnitPrice,
        supplier_id: newItemSupplier || undefined,
        product_id: selectedProduct?.id
      };

      const newItem = await quotesService.addItem(itemData);
      setItems([...items, newItem.data]);
      
      // Reload quote to get updated totals
      const updatedQuote = await quotesService.get(quote.id);
      setQuote(updatedQuote.data);
      
      // Reset form
      setShowAddItem(false);
      setNewItemName('');
      setNewItemDescription('');
      setNewItemQuantity(1);
      setNewItemUnitCost(0);
      setNewItemUnitPrice(0);
      setNewItemSupplier('');
      setSelectedProduct(null);
      
      toast.success('Item added to quote');
    } catch (error) {
      console.error('Error adding item:', error);
      toast.error('Failed to add item');
    }
  };

  const handleSelectProduct = (product: Product) => {
    setSelectedProduct(product);
    setNewItemName(product.name);
    setNewItemDescription(product.description || '');
    setNewItemUnitCost(product.default_cost || 0);
    setNewItemUnitPrice(product.default_price || 0);
    setNewItemSupplier(product.default_supplier_id || '');
  };

  const handleDeleteItem = async (itemId: string) => {
    if (!quote) return;
    
    try {
      await quotesService.deleteItem(itemId);
      setItems(items.filter(i => i.id !== itemId));
      
      // Reload quote to get updated totals
      const updatedQuote = await quotesService.get(quote.id);
      setQuote(updatedQuote.data);
      
      toast.success('Item removed');
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('Failed to remove item');
    }
  };

  const handleSave = async () => {
    if (!quote) return;
    
    try {
      setSaving(true);
      await quotesService.update(quote.id, {
        tax_rate: taxRate,
        discount_amount: discountAmount,
        valid_until: validUntil,
        payment_terms: paymentTerms,
        delivery_terms: deliveryTerms,
        notes,
        create_supplier_solution: createSupplierSolution
      });
      
      toast.success('Quote saved');
    } catch (error) {
      console.error('Error saving quote:', error);
      toast.error('Failed to save quote');
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async () => {
    if (!quote) return;
    
    if (items.length === 0) {
      toast.error('Please add at least one item before sending');
      return;
    }
    
    try {
      setSaving(true);
      await quotesService.send(quote.id);
      toast.success('Quote sent to customer!');
      navigate(`/crm/deals/${dealId}`);
    } catch (error) {
      console.error('Error sending quote:', error);
      toast.error('Failed to send quote');
    } finally {
      setSaving(false);
    }
  };

  const getMarginColor = (margin: number) => {
    if (margin < 20) return 'text-red-600';
    if (margin < 40) return 'text-yellow-600';
    return 'text-green-600';
  };

  const getMarginLabel = (margin: number) => {
    if (margin < 20) return '⚠️ Low Margin';
    if (margin < 40) return '✓ Good Margin';
    return '✅ Great Margin';
  };

  if (loading) {
    return <div className="p-6">Loading quote...</div>;
  }

  if (!quote) {
    return <div className="p-6">Quote not found</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(`/crm/deals/${dealId}`)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Deal
          </button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Build Your Quote
              </h1>
              <p className="text-gray-600 mt-1">
                {deal?.title || 'New Quote'} • Quote #{quote.quote_number}
              </p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
              >
                <Save className="w-4 h-4" />
                Save Draft
              </button>
              <button
                onClick={handleSend}
                disabled={saving || items.length === 0}
                className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
                Send Quote
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Quote Items */}
          <div className="lg:col-span-2 space-y-6">
            {/* Add Item Section */}
            <div className="bg-white rounded-xl shadow-sm ring-1 ring-gray-100 p-6">
              <h2 className="text-lg font-semibold mb-4">What are you selling?</h2>
              
              {!showAddItem ? (
                <button
                  onClick={() => setShowAddItem(true)}
                  className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 transition"
                >
                  <Plus className="w-5 h-5" />
                  Add Product or Service
                </button>
              ) : (
                <div className="space-y-4">
                  {/* Product Selector */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Choose from your products (optional)
                    </label>
                    <select
                      value={selectedProduct?.id || ''}
                      onChange={(e) => {
                        const product = products.find(p => p.id === e.target.value);
                        if (product) handleSelectProduct(product);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Or create a new item below...</option>
                      {products.map(product => (
                        <option key={product.id} value={product.id}>
                          {product.name} - ${product.default_price} (Cost: ${product.default_cost})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Item Details */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Product/Service Name *
                    </label>
                    <input
                      type="text"
                      value={newItemName}
                      onChange={(e) => setNewItemName(e.target.value)}
                      placeholder="e.g., Website Design, SEO Package, Logo Design"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description (optional)
                    </label>
                    <textarea
                      value={newItemDescription}
                      onChange={(e) => setNewItemDescription(e.target.value)}
                      placeholder="Add details about what's included..."
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Quantity
                      </label>
                      <input
                        type="number"
                        value={newItemQuantity}
                        onChange={(e) => setNewItemQuantity(Number(e.target.value))}
                        min="1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Who supplies this?
                      </label>
                      <select
                        value={newItemSupplier}
                        onChange={(e) => setNewItemSupplier(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">No supplier needed</option>
                        {suppliers.map(supplier => (
                          <option key={supplier.id} value={supplier.id}>
                            {supplier.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        What it costs you
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-2.5 text-gray-500">$</span>
                        <input
                          type="number"
                          value={newItemUnitCost}
                          onChange={(e) => setNewItemUnitCost(Number(e.target.value))}
                          min="0"
                          step="0.01"
                          className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">From supplier or your cost</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        What you charge customer
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-2.5 text-gray-500">$</span>
                        <input
                          type="number"
                          value={newItemUnitPrice}
                          onChange={(e) => setNewItemUnitPrice(Number(e.target.value))}
                          min="0"
                          step="0.01"
                          className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Your selling price</p>
                    </div>
                  </div>

                  {/* Margin Preview */}
                  {newItemUnitPrice > 0 && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <div className="text-gray-500">Total Cost</div>
                          <div className="font-semibold">${(newItemUnitCost * newItemQuantity).toFixed(2)}</div>
                        </div>
                        <div>
                          <div className="text-gray-500">Total Price</div>
                          <div className="font-semibold">${(newItemUnitPrice * newItemQuantity).toFixed(2)}</div>
                        </div>
                        <div>
                          <div className="text-gray-500">Your Profit</div>
                          <div className={`font-semibold ${getMarginColor(((newItemUnitPrice - newItemUnitCost) / newItemUnitPrice) * 100)}`}>
                            ${((newItemUnitPrice - newItemUnitCost) * newItemQuantity).toFixed(2)}
                            ({(((newItemUnitPrice - newItemUnitCost) / newItemUnitPrice) * 100).toFixed(1)}%)
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowAddItem(false)}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAddItem}
                      disabled={!newItemName || newItemUnitPrice <= 0}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                    >
                      Add to Quote
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Quote Items List */}
            {items.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm ring-1 ring-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-200">
                  <h2 className="text-lg font-semibold">Items in this quote</h2>
                </div>
                
                <div className="divide-y divide-gray-200">
                  {items.map((item) => (
                    <div key={item.id} className="p-6 hover:bg-gray-50 transition">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900">{item.name}</h3>
                          {item.description && (
                            <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                          )}
                          
                          <div className="mt-3 grid grid-cols-4 gap-4 text-sm">
                            <div>
                              <div className="text-gray-500">Quantity</div>
                              <div className="font-medium">{item.quantity}</div>
                            </div>
                            <div>
                              <div className="text-gray-500">Your Cost</div>
                              <div className="font-medium">${item.unit_cost.toFixed(2)}</div>
                            </div>
                            <div>
                              <div className="text-gray-500">Customer Price</div>
                              <div className="font-medium">${item.unit_price.toFixed(2)}</div>
                            </div>
                            <div>
                              <div className="text-gray-500">Profit Margin</div>
                              <div className={`font-semibold ${getMarginColor(item.margin_percent)}`}>
                                {item.margin_percent.toFixed(1)}%
                              </div>
                            </div>
                          </div>

                          <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between">
                            <div className="text-sm text-gray-600">
                              Total: ${item.line_total.toFixed(2)} 
                              <span className="mx-2">•</span>
                              Profit: <span className={getMarginColor(item.margin_percent)}>
                                ${item.line_profit.toFixed(2)}
                              </span>
                            </div>
                            <span className={`text-xs font-medium px-2 py-1 rounded ${
                              item.margin_percent < 20 ? 'bg-red-100 text-red-700' :
                              item.margin_percent < 40 ? 'bg-yellow-100 text-yellow-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                              {getMarginLabel(item.margin_percent)}
                            </span>
                          </div>
                        </div>
                        
                        <button
                          onClick={() => handleDeleteItem(item.id)}
                          className="ml-4 p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Quote Summary */}
          <div className="space-y-6">
            {/* Totals Card */}
            <div className="bg-white rounded-xl shadow-sm ring-1 ring-gray-100 p-6 sticky top-6">
              <h2 className="text-lg font-semibold mb-4">Quote Summary</h2>
              
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-medium">${quote.subtotal.toFixed(2)}</span>
                </div>
                
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-gray-600">Tax Rate</span>
                    <input
                      type="number"
                      value={taxRate}
                      onChange={(e) => setTaxRate(Number(e.target.value))}
                      className="w-20 px-2 py-1 text-right border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      step="0.1"
                      min="0"
                    />
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Tax Amount ({taxRate}%)</span>
                    <span>${((quote.subtotal * taxRate) / 100).toFixed(2)}</span>
                  </div>
                </div>
                
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-gray-600">Discount</span>
                    <input
                      type="number"
                      value={discountAmount}
                      onChange={(e) => setDiscountAmount(Number(e.target.value))}
                      className="w-24 px-2 py-1 text-right border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      step="10"
                      min="0"
                    />
                  </div>
                </div>
                
                <div className="pt-3 border-t border-gray-200">
                  <div className="flex justify-between text-lg font-bold">
                    <span>Total Quote Value</span>
                    <span>${(quote.subtotal + (quote.subtotal * taxRate / 100) - discountAmount).toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Profit Breakdown */}
              <div className="mt-6 pt-6 border-t border-gray-200 space-y-3">
                <div className="flex items-center gap-2 text-gray-700 font-medium">
                  <TrendingUp className="w-4 h-4" />
                  Your Profit Breakdown
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Cost</span>
                    <span className="font-medium text-red-600">-${quote.total_cost.toFixed(2)}</span>
                  </div>
                  
                  <div className="flex justify-between pt-2 border-t border-gray-200">
                    <span className="text-gray-900 font-medium">Your Profit</span>
                    <span className="font-bold text-green-600">${quote.profit_amount.toFixed(2)}</span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-gray-600">Profit Margin</span>
                    <span className={`font-bold ${getMarginColor(quote.profit_margin_percent)}`}>
                      {quote.profit_margin_percent.toFixed(1)}%
                    </span>
                  </div>
                </div>

                {quote.profit_margin_percent < 20 && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5" />
                      <div className="text-xs text-red-700">
                        <strong>Low margin warning:</strong> This quote has a profit margin below 20%. Consider increasing prices or reducing costs.
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Settings */}
              <div className="mt-6 pt-6 border-t border-gray-200 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valid Until
                  </label>
                  <input
                    type="date"
                    value={validUntil}
                    onChange={(e) => setValidUntil(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Terms
                  </label>
                  <input
                    type="text"
                    value={paymentTerms}
                    onChange={(e) => setPaymentTerms(e.target.value)}
                    placeholder="e.g., Net 30 days"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={createSupplierSolution}
                      onChange={(e) => setCreateSupplierSolution(e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">
                      Create fulfillment plan in Supplier Operations
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1 ml-6">
                    When you win this deal, we'll automatically set up the orders with your suppliers
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuoteBuilder;
